import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Input,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Link as JoyLink,
} from '@mui/joy'
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { authService } from '@/services/authService'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const validateEmail = (): boolean => {
    if (!email.trim()) {
      setEmailError('Email is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address')
      return false
    }
    setEmailError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateEmail()) return

    setIsLoading(true)
    setError('')

    try {
      await authService.forgotPassword(email)
      setIsSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.level1',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent>
          <Box textAlign="center" mb={4}>
            <Typography level="h2" fontWeight="lg" mb={1}>
              {isSuccess ? 'Check your email' : 'Forgot your password?'}
            </Typography>
            {!isSuccess && (
              <Typography level="body-sm" color="neutral">
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
            )}
          </Box>

          {isSuccess ? (
            <Stack spacing={3} alignItems="center">
              <CheckCircle sx={{ fontSize: 48, color: 'success.500' }} />
              <Typography level="body-md" textAlign="center">
                If that email is in our system, you'll receive a reset link shortly. Check your
                inbox (and spam folder).
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                fullWidth
                startDecorator={<ArrowBack />}
              >
                Back to login
              </Button>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl error={!!emailError}>
                  <Typography level="body-sm" mb={1}>
                    Email
                  </Typography>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (emailError) setEmailError('')
                      if (error) setError('')
                    }}
                    error={!!emailError}
                    disabled={isLoading}
                    size="lg"
                    startDecorator={<Email />}
                  />
                  {emailError && <FormHelperText>{emailError}</FormHelperText>}
                </FormControl>

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                  startDecorator={isLoading ? <CircularProgress size="sm" /> : undefined}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Box textAlign="center">
                  <JoyLink
                    component={RouterLink}
                    to="/login"
                    level="body-sm"
                    color="neutral"
                  >
                    Back to login
                  </JoyLink>
                </Box>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
